import React from 'react';
import styled from 'styled-components';
import { Layout } from '../components/layout/Layout';
import { Calendar, Clock, Film } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: var(--spacing-6);
  text-align: center;
  position: relative;
  
  /* Arrière-plan avec image de cinéma */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=1920&q=80');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.08;
    filter: blur(2px);
    z-index: -1;
  }
  
  /* Overlay sombre pour améliorer la lisibilité */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(0, 0, 0, 0.7) 0%,
      rgba(20, 20, 30, 0.85) 50%,
      rgba(0, 0, 0, 0.7) 100%
    );
    z-index: -1;
  }
  
  @media (max-width: 768px) {
    padding: var(--spacing-4);
    min-height: 50vh;
    
    &::before {
      opacity: 0.05;
    }
  }
`;

const IconWrapper = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--color-primary) 0%, #FFA500 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--spacing-4);
  animation: pulse 2s ease-in-out infinite;
  
  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.8;
    }
  }
  
  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-3);
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const MovieTitle = styled.h2`
  font-size: 1.8rem;
  color: var(--color-primary);
  margin-bottom: var(--spacing-4);
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const Description = styled.p`
  font-size: 1.2rem;
  color: var(--color-text-secondary);
  max-width: 600px;
  line-height: 1.6;
  margin-bottom: var(--spacing-4);
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-4);
  max-width: 800px;
  width: 100%;
  margin-top: var(--spacing-6);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-3);
  }
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: var(--spacing-4);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    padding: var(--spacing-3);
  }
`;

const InfoLabel = styled.span`
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const InfoValue = styled.span`
  color: var(--color-primary);
  font-size: 1.1rem;
  font-weight: 600;
`;

const ComingSoon: React.FC = () => {
  const location = useLocation();
  const state = location.state as { title?: string; type?: string } | null;
  
  const movieTitle = state?.title || 'Ce film';
  const mediaType = state?.type === 'tv' ? 'Cette série' : 'Ce film';

  return (
    <Layout>
      <Container>
        <IconWrapper>
          <Film size={60} color="white" />
        </IconWrapper>
        
        <Title>Bientôt à l'affiche</Title>
        
        {movieTitle && movieTitle !== 'Ce film' && (
          <MovieTitle>{movieTitle}</MovieTitle>
        )}
        
        <Description>
          {mediaType} n'est pas encore sorti en salles. 
          La date de sortie n'a pas encore été annoncée officiellement.
        </Description>
        
        <InfoGrid>
          <InfoCard>
            <Calendar size={32} color="var(--color-primary)" />
            <InfoLabel>Statut</InfoLabel>
            <InfoValue>À venir</InfoValue>
          </InfoCard>
          
          <InfoCard>
            <Clock size={32} color="var(--color-primary)" />
            <InfoLabel>Disponibilité</InfoLabel>
            <InfoValue>Prochainement</InfoValue>
          </InfoCard>
          
          <InfoCard>
            <Film size={32} color="var(--color-primary)" />
            <InfoLabel>Type</InfoLabel>
            <InfoValue>{state?.type === 'tv' ? 'Série TV' : 'Film'}</InfoValue>
          </InfoCard>
        </InfoGrid>
      </Container>
    </Layout>
  );
};

export default ComingSoon;
